import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, CheckCircle2, XCircle, Clock, Brain } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface BatchEvalItem {
  id: string;
  name: string;
  company: string | null;
}

type Status = "pending" | "running" | "success" | "error";

interface QueueRow extends BatchEvalItem {
  status: Status;
  score?: number;
  error?: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: BatchEvalItem[];
  delayMs?: number;
}

export function BatchEvaluateDialog({ open, onOpenChange, items, delayMs = 800 }: Props) {
  const [queue, setQueue] = useState<QueueRow[]>([]);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const cancelledRef = useRef(false);
  const queryClient = useQueryClient();

  const wasOpenRef = useRef(false);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      // Only initialize on closed -> open transition.
      setQueue(itemsRef.current.map(i => ({ ...i, status: "pending" })));
      setDone(false);
      cancelledRef.current = false;
    }
    wasOpenRef.current = open;
  }, [open]);

  const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

  const processIds = async (ids: string[]) => {
    setRunning(true);
    for (const id of ids) {
      if (cancelledRef.current) break;
      setQueue(prev => prev.map(r => r.id === id ? { ...r, status: "running", error: undefined } : r));
      try {
        const { data, error } = await supabase.functions.invoke("evaluate-project", {
          body: { project_id: id },
        });
        if (error) {
          let body: any = null;
          try {
            const ctx: any = (error as any).context;
            if (ctx?.json) body = await ctx.json();
            else if (ctx instanceof Response) body = await ctx.clone().json();
            else if (ctx?.body) body = typeof ctx.body === "string" ? JSON.parse(ctx.body) : ctx.body;
          } catch { /* ignore */ }
          let msg = "No pudimos evaluar este proyecto. Inténtalo de nuevo.";
          if (body?.error && !/non-?2xx/i.test(body.error)) msg = body.error;
          throw new Error(msg);
        }
        if (data?.reason === "not_ready") {
          const missing: string[] = Array.isArray(data.missing) ? data.missing : [];
          const parts: string[] = [];
          if (missing.includes("submission")) parts.push("aún no se ha entregado");
          if (missing.includes("rubric")) parts.push("no hay ninguna respuesta de la rúbrica");
          throw new Error(parts.length ? `No está listo para evaluar: ${parts.join(" y ")}.` : "No está listo para evaluar.");
        }

        const score = (data?.total_score ?? data?.evaluation?.total_score ?? data?.score) as number | undefined;
        setQueue(prev => prev.map(r => r.id === id ? { ...r, status: "success", score } : r));
      } catch (e: any) {
        const msg = e?.message || e?.error_description || "Error desconocido";
        setQueue(prev => prev.map(r => r.id === id ? { ...r, status: "error", error: msg } : r));
      }
      await sleep(delayMs);
    }
    setRunning(false);
    setDone(true);
    queryClient.invalidateQueries({ queryKey: ["portfolio-projects"] });
    queryClient.invalidateQueries({ queryKey: ["project-evaluations"] });
  };

  const start = () => {
    cancelledRef.current = false;
    processIds(queue.map(q => q.id));
  };

  const retryFailed = () => {
    const failedIds = queue.filter(q => q.status === "error").map(q => q.id);
    if (!failedIds.length) return;
    setQueue(prev => prev.map(r => failedIds.includes(r.id) ? { ...r, status: "pending", error: undefined } : r));
    setDone(false);
    cancelledRef.current = false;
    processIds(failedIds);
  };

  const completed = queue.filter(q => q.status === "success" || q.status === "error").length;
  const successCount = queue.filter(q => q.status === "success").length;
  const errorCount = queue.filter(q => q.status === "error").length;
  const total = queue.length;
  const pct = total ? Math.round((completed / total) * 100) : 0;
  const notStarted = !running && !done && queue.every(q => q.status === "pending");

  useEffect(() => {
    if (done && total > 0) {
      toast.success(`Evaluación batch finalizada: ${successCount} OK · ${errorCount} con error`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  const handleOpenChange = (v: boolean) => {
    if (running) return; // bloquear cierre mientras corre
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-2xl w-[95vw] sm:w-full max-h-[85vh] p-0 flex flex-col overflow-hidden gap-0"
        onPointerDownOutside={e => running && e.preventDefault()}
        onEscapeKeyDown={e => running && e.preventDefault()}
      >
        <DialogHeader className="px-6 pt-6 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-primary" />
            Evaluar proyectos con IA
          </DialogTitle>
          <DialogDescription>
            Las evaluaciones se ejecutan una por una para evitar errores. No cierres esta ventana mientras esté corriendo.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 flex flex-col gap-3 px-6 py-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {completed} / {total} procesados
            </span>
            <span className="font-medium">
              <span className="text-success">{successCount} OK</span>
              {errorCount > 0 && <span className="text-destructive ml-2">{errorCount} error</span>}
            </span>
          </div>
          <Progress value={pct} />

          <div className="flex-1 min-h-0 overflow-y-auto rounded-md border p-2">
            <ul className="space-y-1.5">
              {queue.map(row => (
                <li
                  key={row.id}
                  className={`flex items-start gap-3 p-2 rounded-md text-sm ${
                    row.status === "running" ? "bg-primary/10 border border-primary/30" : "hover:bg-muted/40"
                  }`}
                >
                  <div className="mt-0.5">
                    {row.status === "pending" && <Clock className="w-4 h-4 text-muted-foreground" />}
                    {row.status === "running" && <Loader2 className="w-4 h-4 text-primary animate-spin" />}
                    {row.status === "success" && <CheckCircle2 className="w-4 h-4 text-success" />}
                    {row.status === "error" && <XCircle className="w-4 h-4 text-destructive" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{row.name}</div>
                    {row.company && (
                      <div className="text-xs text-muted-foreground truncate">{row.company}</div>
                    )}
                    {row.status === "error" && row.error && (
                      <div className="text-xs text-destructive mt-0.5 break-words">{row.error}</div>
                    )}
                    {row.status === "success" && typeof row.score === "number" && (
                      <div className="text-xs text-success mt-0.5">Score: {row.score}</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 gap-2 flex-shrink-0 border-t bg-background">
          {notStarted && (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button onClick={start} className="gap-2">
                <Brain className="w-4 h-4" />
                Iniciar evaluación ({total})
              </Button>
            </>
          )}
          {running && (
            <Button disabled className="gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Evaluando…
            </Button>
          )}
          {done && (
            <>
              {errorCount > 0 && (
                <Button variant="outline" onClick={retryFailed}>
                  Reintentar fallidos ({errorCount})
                </Button>
              )}
              <Button onClick={() => onOpenChange(false)}>Cerrar</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
